import { styled } from "@linaria/react";
import * as React from "react";
import * as io from "io-ts";
import { useNavigate, useOutletContext } from "react-router-dom";
import t from "../../../i18n";
import { minutesToFractional } from "../../../lib/time/minutes";
import { withKeyNotNull } from "diagnostics-lib/typescript";
import { color, fonts } from "@ui";
import Button from "@ui/Button";
import SecondaryHeader from "@ui/Header/SecondaryHeader";
import Link from "@ui/Link";
import Modal from "@ui/Modal";
import Section from "@ui/Section";
import SidePanelLayout from "@ui/SidePanel/Layout";
import type { Project } from "../../../api/useProjects";

import { useQuantities } from "../../../api/useQuantities";
import AnalysisFrequencyModalForm from "./modalForm/AnalysisFrequencyModalForm";
import LoadFiltersModalForm from "./ActivationFilters/LoadFiltersModalForm";
import useActivationFilters from "../../../api/useActivationFilters";
import ManageQoiModalForm, { defaultQoiRolesOptions } from "./modalForm/ManageQoiModalForm";
import CreateUpdateModalForm from "./Setup/CreateUpdateModalForm";
import { useSetups } from "../../../api/useSetups";
import SetupTable from "./Setup/Table";
import { useCanUserDoOnProject } from "../../canUserDo";
import ManageProcessIndicatorModalForm from "./modalForm/ManageProcessIndicatorModalForm";
import {
  AddCircleLine,
  CheckboxBlankCircleFill,
  EyeLine,
  InformationLine,
  PencilLine,
  PlayCircleLine,
  StopCircleLine,
} from "@ui/IconV2/react";
import BlankStateV2 from "@ui/BlankStateV2";
import Text from "@ui/Text";
import Badge from "@ui/Badge";
import ChangeDatasourceModalForm from "./modalForm/ChangeDatasourceModalForm";
import ChangeTimezoneModalForm from "./modalForm/ChangeTimezoneModalForm";
import DataFlowPausedModalForm from "./modalForm/DataFlowPausedModalForm";
import DataFlowActivatedModalForm from "./modalForm/DataFlowActivatedModalForm";
import createGlobalState from "@ui/lib/createGlobalState";
import { comparatorBy } from "diagnostics-lib/comparator";
import { toPath } from "../../../router/helpers/path";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { css } from "@linaria/core";
import DisplayFiltersModalForm from "./DisplayFilters/ModalForm/DisplayFiltersModalForm";
import { useDisplayFilters } from "../../../api/useDisplayFilters";
import { spacings } from "@ui/Theme";
import Switch from "@ui/Switch";
import { usePut } from "../../../api/useCall";
import { useMutateBody } from "../../../api/useQuery";
import ErrorBoundary from "../../ErrorBoundary";
import LoadingBoundary from "@ui/LoadingBoundary";

const Title = styled(Text)`
  padding-bottom: 16px;
  font: ${fonts.h3};
  color: ${color.carbon900};
`;
const BlockTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SubTitle = styled(Text)`
  font: ${fonts.h4};
  color: ${color.carbon700};
`;
const BlockSubTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledSidePanelLayoutCustom = styled(SidePanelLayout)`
  gap: 0;
  flex: none;

  ${BlockSubTitle}:not(:last-child) {
    margin-bottom: 32px;
  }

  ${BlockTitle}:not(:last-child) {
    margin-bottom: 48px;
  }
`;

const BlankCard = styled(BlankStateV2)`
  margin-top: 8px;
  border: 1px solid #e0e3eb;
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  font: ${fonts.body1};
  color: ${color.lightNitrogen500_Main};
  gap: ${spacings.xxs};
  :hover {
    text-decoration: underline;
    color: ${color.lightNitrogen600};
  }
`;

const LinkButton = styled(Button)`
  height: auto;
`;

export interface Qoi {
  name: string;
  order: number;
}

const enabledBadgeProps = {
  backgroundColor: color.chlorine100,
  icon: <CheckboxBlankCircleFill size="xxs" color={color.chlorine600} />,
};

const disabledBadgeProps = {
  backgroundColor: color.oxygen50,
  icon: <CheckboxBlankCircleFill size="xxs" color={color.oxygen400} />,
};

const Block = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <>
    <Title>{title}</Title>
    <BlockTitle>{children}</BlockTitle>
  </>
);

const ModalOption = io.union([
  io.literal("analysisActivationFlow"),
  io.literal("analysesFiltersLoad"),
  io.literal("displayFilters"),
  io.literal("analysesFrequencyUpdate"),
  io.literal("create"),
  io.literal("datasource"),
  io.literal("manageProcessIndicators"),
  io.literal("manageQoi"),
  io.literal("timezone"),
  io.literal("dataFlowActivated"),
  io.literal("dataFlowDisabled"),
  io.undefined,
]);
type ModalOption = io.TypeOf<typeof ModalOption>;

enum ActivationFlow {
  ACTIVATED,
  DISABLED,
}

export const useModalSettingManagement = createGlobalState({
  storage: "session",
  key: "launchAnalysisSelection",
  type: ModalOption,
  defaultValue: undefined,
  scoped: true,
});

const useModalSettings = (project: Project) => {
  const [settingModal, setSettingModal] = useModalSettingManagement({ scope: project.id });
  const [modal, setModal] = React.useState<ModalOption>();

  const openModal = React.useCallback(
    (modalType: ModalOption) => {
      setModal(modalType);
    },
    [setModal],
  );
  const closeModal = React.useCallback(() => {
    setModal(undefined);
    setSettingModal(undefined);
  }, [setModal, setSettingModal]);

  React.useEffect(() => {
    if (settingModal) {
      openModal(settingModal);
    }
  }, [settingModal, openModal]);

  return {
    modal,
    openModal,
    closeModal,
  };
};

export const Settings = () => {
  const project = useOutletContext<Project>();
  if (!project) throw new Error("no project");

  const navigate = useNavigate();
  const { modal, closeModal, openModal } = useModalSettings(project);
  const canEditSetup = useCanUserDoOnProject("PROJECT_SETUP", project.id);
  const setups = useSetups(project.id);
  const quantities = useQuantities(project.id);
  const displayFilters = useDisplayFilters(project.id);
  const filters = useActivationFilters(project.id);
  const updateProject = usePut("/projects/:projectId(uuid)");
  const mutateProjects = useMutateBody("/projects");
  const analysisPeriod = project.minutesBetweenActiveAnalysis;
  const splittedInterval = minutesToFractional(analysisPeriod);

  const updatePartLoadSensitiveKpisDisplay = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const showPartLoadSensitiveKpis = event.currentTarget.checked;
      const rollback = mutateProjects(body => ({
        ...body,
        collection: body.collection.map(projectItem =>
          projectItem.id === project.id
            ? { ...projectItem, showPartLoadSensitiveKpis }
            : projectItem,
        ),
      }));

      try {
        await updateProject({
          body: { ...project, showPartLoadSensitiveKpis },
          routeParams: { projectId: project.id },
        });
      } catch (error) {
        rollback();
        throw error;
      }
    },
    [mutateProjects, project, updateProject],
  );

  const groupsCount = displayFilters.reduce(
    (groups, displayFilter) =>
      displayFilter.group === null ? groups : groups.add(displayFilter.group),
    new Set<string>(),
  ).size;

  const sensorTags = React.useMemo(
    () => quantities.filter(quantity => quantity.sensor !== null),
    [quantities],
  );

  const computedQuantites = React.useMemo(
    () => quantities.filter(quantity => quantity.formula !== null),
    [quantities],
  );

  const qois = React.useMemo(
    () =>
      quantities
        .filter(withKeyNotNull("qoiOrder"))
        .sort(comparatorBy("qoiOrder"))
        .map(qoi => ({
          id: qoi.id,
          qoiOrder: qoi.qoiOrder,
          selectedQoiRole: qoi.qoiRole || null,
          qoiReferenceValue: qoi.qoiReferenceValue,
          name: qoi.name,
        })),
    [quantities],
  );

  const qoiList = React.useMemo(
    () =>
      qois
        .map(
          qoi =>
            `${qoi.name}${
              qoi.selectedQoiRole
                ? ` (${
                    defaultQoiRolesOptions.find(el => el.value === qoi.selectedQoiRole)?.label || ""
                  })`
                : ""
            }`,
        )
        .join(", "),
    [qois],
  );

  const processIndicators = React.useMemo(
    () =>
      quantities
        .filter(withKeyNotNull("processIndicatorOrder"))
        .sort(comparatorBy("processIndicatorOrder"))
        .map(qoi => ({
          id: qoi.id,
          processIndicatorOrder: qoi.processIndicatorOrder,
          name: qoi.name,
        })),
    [quantities],
  );
  const processIndicatorsList = React.useMemo(
    () => processIndicators.map(qoi => qoi.name).join(", "),
    [processIndicators],
  );

  const analysisFlow = project.hasActivatedDataFlow
    ? ActivationFlow.ACTIVATED
    : ActivationFlow.DISABLED;

  return (
    <>
      <SecondaryHeader breadcrumb={t("project.secondary_header.settings.title")} />
      <StyledSidePanelLayoutCustom>
        <Block title={t("project.settings.data.title")}>
          <SubTitle>{t("project.settings.data_definition.subtitle")}</SubTitle>
          <BlockSubTitle>
            <Field>
              <Text>
                {t("project.settings.data.name")} {project.sourceId}
              </Text>
              {canEditSetup && (
                <LinkButton
                  variant="link"
                  onClick={() => openModal("datasource")}
                  leftIcon={<PencilLine />}
                >
                  {t("project.settings.link.text")}
                </LinkButton>
              )}
            </Field>

            <Field>
              <Text>
                {t("project.settings.quantities.name")}
                {t("project.settings.computed_sensors_quantities_count", {
                  sensorCount: sensorTags.length,
                  quantityCount: computedQuantites.length,
                })}
              </Text>
              <StyledLink
                to={toPath("/projects/:projectId/settings/quantities", {
                  projectId: project.id,
                })}
              >
                <EyeLine />
                {t("project.settings.data_definition.subtitle.field.quantity")}
              </StyledLink>
            </Field>
          </BlockSubTitle>

          <SubTitle>{t("project.settings.display.subtitle")}</SubTitle>
          <BlockSubTitle>
            <Field>
              <Switch
                checked={project.showPartLoadSensitiveKpis}
                disabled={!canEditSetup}
                loading={updateProject.loading}
                onChange={updatePartLoadSensitiveKpisDisplay}
                tooltipOverlay={t("project.settings.display.part_load_sensitive_kpis.tooltip")}
              >
                {t("project.settings.display.part_load_sensitive_kpis")}
              </Switch>
            </Field>
            <Field>
              <Text>{t("project.settings.timezone", { timeZone: project.timezone })}</Text>
              {canEditSetup && (
                <LinkButton
                  variant="link"
                  onClick={() => openModal("timezone")}
                  leftIcon={<PencilLine />}
                >
                  {t("project.settings.link.text")}
                </LinkButton>
              )}
            </Field>
            <Field>
              <Text>{t("project.settings.qoi.name", { qoiList })}</Text>
              {canEditSetup && (
                <LinkButton
                  variant="link"
                  onClick={() => openModal("manageQoi")}
                  leftIcon={<PencilLine />}
                >
                  {t("project.settings.qoi.edit")}
                </LinkButton>
              )}
            </Field>
            <Field>
              <Text>{t("project.settings.processIndicators.name", { processIndicatorsList })}</Text>
              {canEditSetup ? (
                <LinkButton
                  variant="link"
                  onClick={() => openModal("manageProcessIndicators")}
                  leftIcon={<PencilLine />}
                >
                  {t("project.settings.processIndicator.edit")}
                </LinkButton>
              ) : null}
            </Field>
            <Field>
              <Text>
                {t("project.settings.display_filters.name", {
                  filterCount: displayFilters.length,
                  groupCount: groupsCount,
                })}
              </Text>
              <TooltipV2
                placement="top"
                overlay={
                  <OverlayWrapper
                    className={overlayWidth}
                    text={t("project.settings.display_filters.tooltip")}
                  />
                }
              >
                <span className={centerIcon}>
                  <InformationLine size="s" color={color.carbon800} />
                </span>
              </TooltipV2>
              {displayFilters.length > 0 ? (
                <StyledLink
                  to={toPath("/projects/:projectId/settings/display-filters", {
                    projectId: project.id,
                  })}
                >
                  {canEditSetup ? (
                    <>
                      <PencilLine />
                      {t("project.settings.analyses.edit_filters")}
                    </>
                  ) : (
                    <>
                      <EyeLine />
                      {t("project.settings.analyses.show_filters")}
                    </>
                  )}
                </StyledLink>
              ) : canEditSetup ? (
                <LinkButton
                  variant="link"
                  onClick={() => openModal("displayFilters")}
                  leftIcon={<AddCircleLine />}
                >
                  {t("project.settings.analyses.add_filters")}
                </LinkButton>
              ) : null}
            </Field>
          </BlockSubTitle>
        </Block>

        <Block title={t("project.settings.analyses.title")}>
          <BlockSubTitle>
            {setups.length > 0 ? (
              <Section
                id="setups"
                title={<SubTitle>{t("project.settings.analyses.subtitle.setup")}</SubTitle>}
                actions={
                  canEditSetup && (
                    <Button
                      variant="primary"
                      onClick={() => openModal("create")}
                      leftIcon={<AddCircleLine />}
                    >
                      {t("project.settings.add_new_setup.button")}
                    </Button>
                  )
                }
              >
                <ErrorBoundary>
                  <LoadingBoundary>
                    <SetupTable setups={setups} project={project} timeZone={project.timezone} />
                  </LoadingBoundary>
                </ErrorBoundary>
              </Section>
            ) : (
              <BlankCard
                title={t("project.settings.blank_state.add_setup.title")}
                subtitle={t("project.settings.blank_state.add_setup.text")}
                onClick={canEditSetup ? () => openModal("create") : undefined}
                buttonLabel={canEditSetup ? t("project.settings.add_setup.button") : undefined}
              />
            )}
          </BlockSubTitle>
          <SubTitle>{t("project.settings.analyses.subtitle.data_flow")}</SubTitle>
          <BlockSubTitle>
            <Field>
              <Text>{t("project.settings.analyses.subtitle.data_flow.field.activation")} : </Text>{" "}
              <Badge
                color={color.carbon900}
                {...(analysisFlow === ActivationFlow.ACTIVATED
                  ? enabledBadgeProps
                  : disabledBadgeProps)}
              >
                {analysisFlow === ActivationFlow.ACTIVATED
                  ? t("project.settings.data_flow.status.activated")
                  : t("project.settings.data_flow.status.disabled")}
              </Badge>
              {canEditSetup ? (
                <LinkButton
                  variant="link"
                  onClick={() =>
                    analysisFlow === ActivationFlow.ACTIVATED
                      ? openModal("dataFlowDisabled")
                      : openModal("dataFlowActivated")
                  }
                  leftIcon={
                    analysisFlow === ActivationFlow.ACTIVATED ? (
                      <StopCircleLine />
                    ) : (
                      <PlayCircleLine />
                    )
                  }
                >
                  {analysisFlow === ActivationFlow.ACTIVATED
                    ? t("project.settings.data_flow.status.activated.link.text")
                    : t("project.settings.data_flow.status.disabled.link.text")}
                </LinkButton>
              ) : null}
            </Field>
            <Field>
              <Text>
                {t("project.settings.analyses.description")}{" "}
                {analysisPeriod === 0
                  ? t("project.settings.none")
                  : t("project.settings.analyses.frequency_value", splittedInterval)}
              </Text>
              {canEditSetup ? (
                <LinkButton
                  variant="link"
                  onClick={() => openModal("analysesFrequencyUpdate")}
                  leftIcon={<PencilLine />}
                >
                  {t("project.settings.analyses.subtitle.data_flow.field.interval")}
                </LinkButton>
              ) : null}
            </Field>
            <Field>
              <Text>{t("project.settings.analyses.filters", { filterCount: filters.length })}</Text>
              <TooltipV2
                placement="top"
                overlay={
                  <OverlayWrapper
                    className={overlayWidth}
                    text={t("project.settings.filters.tooltip")}
                  />
                }
              >
                <span className={centerIcon}>
                  <InformationLine size="s" color={color.carbon800} />
                </span>
              </TooltipV2>
              {filters.length > 0 ? (
                <StyledLink
                  to={toPath("/projects/:projectId/settings/filters", {
                    projectId: project.id,
                  })}
                >
                  {canEditSetup ? (
                    <>
                      <PencilLine />
                      {t("project.settings.analyses.edit_filters")}
                    </>
                  ) : (
                    <>
                      <EyeLine />
                      {t("project.settings.analyses.show_filters")}
                    </>
                  )}
                </StyledLink>
              ) : canEditSetup ? (
                <LinkButton
                  variant="link"
                  onClick={() => openModal("analysesFiltersLoad")}
                  leftIcon={<AddCircleLine />}
                >
                  {t("project.settings.analyses.add_filters")}
                </LinkButton>
              ) : null}
            </Field>
          </BlockSubTitle>
        </Block>
      </StyledSidePanelLayoutCustom>
      {/* Modals */}
      <Modal
        title={t("setups.modal.add_or_edit.create_setup.create")}
        isOpen={modal === "create"}
        onRequestClose={closeModal}
      >
        <CreateUpdateModalForm
          setups={setups}
          projectId={project.id}
          onRequestClose={closeModal}
          timeZone={project.timezone}
          okButtonName={t("modalForm.button.name.create")}
        />
      </Modal>
      <Modal
        title={t("setups.modal.update_analyses_frequency.title")}
        subtitle={t("setups.modal.update_analyses_frequency.description")}
        isOpen={modal === "analysesFrequencyUpdate"}
        onRequestClose={closeModal}
        size="l"
      >
        <AnalysisFrequencyModalForm
          project={project}
          analysisPeriod={analysisPeriod}
          onRequestClose={closeModal}
        />
      </Modal>
      <Modal
        title={t("project.settings.analyses.add_filters.modal.title")}
        subtitle={t("project.settings.analyses.add_filters.modal.description")}
        isOpen={modal === "analysesFiltersLoad"}
        onRequestClose={closeModal}
      >
        <LoadFiltersModalForm
          isEdit={false}
          projectId={project.id}
          closeLoadFiltersModal={closeModal}
          onLoadFilterClose={() => {
            closeModal();
            void navigate(
              toPath("/projects/:projectId/settings/filters", {
                projectId: project.id,
              }),
            );
          }}
        />
      </Modal>
      <Modal
        title={t("project.settings.analyses.add_display_filters.modal.title")}
        isOpen={modal === "displayFilters"}
        onRequestClose={closeModal}
      >
        <DisplayFiltersModalForm projectId={project.id} onRequestClose={closeModal} needRedirect />
      </Modal>
      <Modal
        title={t("project.settings.qoi.modal.title", { editMode: canEditSetup })}
        isOpen={modal === "manageQoi"}
        onRequestClose={closeModal}
        subtitle={t("project.settings.performance_indicators.max.additionnal_message")}
        size="xl"
      >
        <ManageQoiModalForm
          projectId={project.id}
          quantities={quantities}
          qois={qois}
          closeModal={closeModal}
        />
      </Modal>
      <Modal
        title={t("project.settings.processIndicators.modal.title", { editMode: canEditSetup })}
        isOpen={modal === "manageProcessIndicators"}
        onRequestClose={closeModal}
        subtitle={t("project.settings.process_indicators.max.additionnal_message")}
      >
        <ManageProcessIndicatorModalForm
          projectId={project.id}
          quantities={quantities}
          processIndicators={processIndicators}
          closeModal={closeModal}
        />
      </Modal>
      <Modal
        title={t("project.settings.datasource.modal.title")}
        isOpen={modal === "datasource"}
        onRequestClose={closeModal}
      >
        <ChangeDatasourceModalForm
          project={project}
          preselectedSource={project.sourceId}
          onRequestClose={closeModal}
        />
      </Modal>
      <Modal
        title={t("project.settings.timezone.modal.title")}
        isOpen={modal === "timezone"}
        onRequestClose={closeModal}
      >
        <ChangeTimezoneModalForm project={project} onRequestClose={closeModal} />
      </Modal>
      <Modal
        title={t("project.settings.data_flow.activated.modal.title")}
        subtitle={t("project.settings.data_flow.activated.modal.subtitle")}
        isOpen={modal === "dataFlowActivated"}
        onRequestClose={closeModal}
        size="l"
      >
        <DataFlowActivatedModalForm
          project={project}
          nbFilters={filters.length}
          onRequestClose={closeModal}
        />
      </Modal>
      <Modal
        title={t("project.settings.data_flow.disabled.modal.title")}
        subtitle={t("project.settings.data_flow.disabled.modal.subtitle")}
        isOpen={modal === "dataFlowDisabled"}
        onRequestClose={closeModal}
        size="m"
      >
        <DataFlowPausedModalForm projectId={project.id} onRequestClose={closeModal} />
      </Modal>
    </>
  );
};
export default React.memo(Settings);

//#region CSS
const centerIcon = css`
  svg {
    display: block;
  }
`;
const overlayWidth = css`
  max-width: 279px;
`;
//#endregion CSS
